import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api";
import type { Dayjs } from "dayjs";

export interface AuditLogEntry {
  id: number;
  created_at: Dayjs;
  user_id?: number;
  username?: string;
  display_name?: string;
  action: string;
  entity_type?: string;
  entity_id?: string;
  details?: string;
  ip?: string;
}

export interface AuditLogListResponse {
  items: AuditLogEntry[];
  next_cursor: string | null;
  total_estimate: number;
}

export interface AuditLogFilters {
  from?: string;
  to?: string;
  user_id?: number;
  entity_type?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface AuditLogUser {
  id: number;
  username: string;
  display_name: string;
}

interface AuditLogUsersResponse {
  users: AuditLogUser[];
}

export function useAuditLogs(filters: AuditLogFilters) {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.user_id) params.set("user_id", String(filters.user_id));
  if (filters.entity_type) params.set("entity_type", filters.entity_type);
  if (filters.q) params.set("q", filters.q);
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));

  const qs = params.toString();

  return useQuery<AuditLogListResponse>({
    queryKey: ["audit-logs", filters],
    queryFn: () => apiGet<AuditLogListResponse>(`/audit-logs${qs ? "?" + qs : ""}`),
  });
}

export function useAuditLogUsers() {
  return useQuery<AuditLogUser[]>({
    queryKey: ["audit-log-users"],
    queryFn: async () => {
      const res = await apiGet<AuditLogUsersResponse>("/audit-logs/users");
      return res.users;
    },
    staleTime: 5 * 60 * 1000,
  });
}
